import { compareNames, matchesSearch, type ColumnSort } from "@optymyse/suite-ui";

/**
 * How Link's lists are ordered and searched, as every list in the suite is (SuiteUi's README, "Lists"): A to Z by name
 * to start, a column's name sorting by it and, chosen again, the other way, and one search above the list, case and
 * accents aside. The address keeps both (`?sort=-updated&q=brook`).
 *
 * SuiteServer's client asks Link for its pages most recently changed first, so for now the page holds each list whole,
 * every page of it up to its cap (the Worker's src/domains/directory/whole-list.ts), and sorts and searches it here,
 * with the kit's `compareNames` and `matchesSearch`, which order and match as Unlock's API does. This module is where
 * that changes: once SuiteServer's client asks for Link's own A to Z (`sort=name`, served since 23 October), a list
 * asks its route for one page at a time in the address's order, the route passes it through, and nothing that draws a
 * list changes.
 */

export type Direction = "ascending" | "descending";

/** How a list is sorted: by one of its columns, one way or the other. */
export interface ListSort<C extends string> {
  readonly column: C;
  readonly direction: Direction;
}

/** A column a list sorts by: names in the suite's order, times newest last (first, the other way). */
export type Column<T> =
  | { readonly kind: "name"; readonly value: (row: T) => string | null }
  | { readonly kind: "time"; readonly value: (row: T) => number | null };

/** The sort the address names (`name`, `-updated`), or A to Z by `fallback`'s column when it names none of `columns`. */
export function sortFrom<C extends string>(value: unknown, columns: readonly C[], fallback: C): ListSort<C> {
  if (typeof value === "string") {
    const descending = value.startsWith("-");
    const column = (descending ? value.slice(1) : value) as C;
    if (columns.includes(column)) return { column, direction: descending ? "descending" : "ascending" };
  }
  return { column: fallback, direction: "ascending" };
}

/** The sort as the address writes it, or nothing for the list's own order, A to Z by `fallback`. */
export function sortParam<C extends string>(sort: ListSort<C>, fallback: C): string | undefined {
  if (sort.column === fallback && sort.direction === "ascending") return undefined;
  return `${sort.direction === "descending" ? "-" : ""}${sort.column}`;
}

/** Choosing a column's name: by it, and chosen again, the other way. */
export function nextSort<C extends string>(sort: ListSort<C>, column: C): ListSort<C> {
  if (sort.column !== column) return { column, direction: "ascending" };
  return { column, direction: sort.direction === "ascending" ? "descending" : "ascending" };
}

/** What a column's header needs to sort the list by it (SuiteUi's TableHead `sort`). */
export function columnSort<C extends string>(
  sort: ListSort<C>,
  column: C,
  onSort: (next: ListSort<C>) => void,
): ColumnSort {
  return {
    direction: sort.column === column ? sort.direction : "none",
    onSort: () => onSort(nextSort(sort, column)),
  };
}

/** Two values of a column, a missing one after every other whichever way the list is sorted. */
function compareBy<T>(column: Column<T>, a: T, b: T, sign: 1 | -1): number {
  if (column.kind === "name") {
    const x = column.value(a);
    const y = column.value(b);
    // compareNames puts a missing name last, and it stays last the other way round too.
    return !x?.trim() || !y?.trim() ? compareNames(x, y) : compareNames(x, y) * sign;
  }
  const x = column.value(a);
  const y = column.value(b);
  if (x === null || y === null) return x === y ? 0 : x === null ? 1 : -1;
  return (x - y) * sign;
}

/**
 * The rows a list shows: those the search finds, in the order chosen, ties in the order of `tiebreak`'s column (the
 * name), so the same list reads the same every time.
 */
export function arrange<T, C extends string>(
  rows: readonly T[],
  options: {
    readonly q: string;
    readonly sort: ListSort<C>;
    readonly columns: Readonly<Record<C, Column<T>>>;
    readonly tiebreak: C;
    /** What the search looks in. */
    readonly searched: (row: T) => readonly (string | null | undefined)[];
  },
): T[] {
  const sign = options.sort.direction === "descending" ? -1 : 1;
  const column = options.columns[options.sort.column];
  const tiebreak = options.columns[options.tiebreak];
  return rows
    .filter((row) => matchesSearch(options.q, ...options.searched(row)))
    .sort((a, b) => compareBy(column, a, b, sign) || compareBy(tiebreak, a, b, 1));
}
